import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
  LinearProgress,
  Card,
  CardContent,
  Divider,
  Skeleton,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
  Stack,
  IconButton,
  Tooltip,
  Fade,
  alpha,
  Chip,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  MonetizationOn as RevenueIcon,
  People as PatientsIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  SelfImprovement as DoctorIcon,
  ErrorOutline as ErrorIcon,
  TrendingUp,
  TrendingDown,
  ArrowForward,
  Notifications,
  LocalHospital,
  Assessment,
  MoreVert,
  VideoCall,
  Chat,
  MedicalServices,
  Event,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAppointments } from '../../features/appointments/appointmentsSlice';
import { 
  fetchDashboardStats, 
  fetchDashboardTrends, 
  fetchConsultationDistribution 
} from '../../features/doctors/dashboardSlice';
import { useSubscription } from '../../hooks/useSubscription';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart,
} from 'recharts';
import styled from '@emotion/styled';

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: 16,
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  background: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, 0.1)}`,
  },
}));

const GlassCard = styled(Card)(({ theme }) => ({
  background: alpha(theme.palette.background.paper, 0.7),
  backdropFilter: 'blur(10px)',
  borderRadius: 16,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: alpha(theme.palette.background.paper, 0.8),
    transform: 'translateY(-4px)',
    boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, 0.1)}`,
  },
}));

const PatientAvatar = styled(Avatar)(({ theme }) => ({
  width: 48,
  height: 48,
  backgroundColor: theme.palette.primary.main,
  fontSize: '1.2rem',
  fontWeight: 600,
  transition: 'transform 0.2s ease',
  '&:hover': {
    transform: 'scale(1.1)',
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(1, 2),
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
  },
}));

const DoctorDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const { user } = useSelector((state) => state.auth);
  const appointmentsState = useSelector((state) => state.appointments);
  const appointments = appointmentsState?.data || appointmentsState?.results || [];
  const appointmentsLoading = appointmentsState?.loading || false;
  const appointmentsError = appointmentsState?.error || null;
  
  // Debug logging
  useEffect(() => {
    console.log('📋 Overview Appointments State:', {
      appointments: appointments.length,
      rawState: appointmentsState
    });
  }, [appointments.length, appointmentsState]);
  
  // Get dashboard data from Redux store
  const { 
    stats: dashboardStats, 
    trends: dashboardTrends, 
    consultationDistribution: consultationTypes 
  } = useSelector((state) => state.doctorDashboard);

  // Debug logging for dashboard stats
  useEffect(() => {
    if (dashboardStats?.data) {
      console.log('📊 Overview Dashboard Stats:', {
        today: dashboardStats.data.today,
        pending: dashboardStats.data.pending,
        confirmed: dashboardStats.data.confirmed,
        completed: dashboardStats.data.completed,
        canceled: dashboardStats.data.canceled,
        loading: dashboardStats.loading,
        error: dashboardStats.error
      });
    }
  }, [dashboardStats]);

  // Get subscription data
  const {
    hasActiveSubscription,
    isOnTrial,
    getPlanType,
    getDaysRemaining,
    getCurrentUsage,
    getUsagePercentage,
    isUsageLimitNear,
    isUsageLimitReached
  } = useSubscription();
  
  const [errorMessage, setErrorMessage] = useState('');

  const handleStartConsultation = (appointment) => {
    if (!appointment?.id || !appointment?.patient) {
      toast.error("Unable to start consultation. Please try again.", {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    // Use enhanced consultation for better features
    const patientId = appointment.patient?.id || appointment.patient || appointment.patient_id;
    navigate(`/doctor-dashboard/enhanced-consultation/${appointment.id}/${patientId}`);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch all appointments for the dashboard
        await dispatch(fetchAppointments({ 
          page: 1, 
          page_size: 100 
        })).unwrap();
        
        // Fetch dashboard data
        const [statsResponse] = await Promise.all([
          dispatch(fetchDashboardStats()).unwrap(),
          dispatch(fetchDashboardTrends(6)).unwrap(),
          dispatch(fetchConsultationDistribution()).unwrap()
        ]);
        
        // Debug logging
        console.log('📊 Dashboard stats received:', {
          today: statsResponse?.today,
          pending: statsResponse?.pending,
          confirmed: statsResponse?.confirmed,
          completed: statsResponse?.completed,
          canceled: statsResponse?.canceled
        });
      } catch (error) {
        console.error('❌ Error fetching dashboard data:', error);
        setErrorMessage('Unable to load dashboard data. Please try again later.');
      }
    };
    fetchData();
  }, [dispatch]);

  // Get today's date in YYYY-MM-DD format
  const todayDate = format(new Date(), 'yyyy-MM-dd');

  // Filter today's appointments & sort by time
  const todayAppointments = (appointments || [])
    .filter(appt => {
      if (!appt.scheduled_date) return false;
      try {
        const appointmentDate = typeof appt.scheduled_date === 'string' 
          ? format(new Date(appt.scheduled_date), 'yyyy-MM-dd')
          : format(appt.scheduled_date, 'yyyy-MM-dd');
        return appointmentDate === todayDate && appt.status !== 'Canceled';
      } catch (e) {
        console.error('Error parsing appointment date:', e, appt);
        return false;
      }
    })
    .sort((a, b) => {
      try {
        const timeA = a.scheduled_time || '00:00';
        const timeB = b.scheduled_time || '00:00';
        return new Date(`1970-01-01T${timeA}`) - new Date(`1970-01-01T${timeB}`);
      } catch (e) {
        return 0;
      }
    });

  // Categorize appointments
  const confirmedAppointments = (appointments || []).filter(appt => appt.status === 'Confirmed');
  const pendingAppointments = (appointments || []).filter(appt => appt.status === 'Pending');
  const canceledAppointments = (appointments || []).filter(appt => appt.status === 'Canceled');

  // Dynamic Greeting & Time of Day Logic
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const hasTitle = /^(Dr\.|Doctor|Mr\.|Ms\.)/i.test(user?.name || '');
  const greetingName = hasTitle ? user?.name : `Dr. ${user?.name || 'User'}`;

  const StatCard = ({ title, value, icon, color, loading, trend, trendValue }) =>
    <StyledCard>
      <CardContent sx={{ p: 3 }}>
        {loading ? (
          <>
            <Skeleton variant="circular" width={48} height={48} sx={{ mb: 2 }} />
            <Skeleton variant="text" width="60%" sx={{ mb: 1 }} />
            <Skeleton variant="text" width="40%" />
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette[color].main, 0.1),
                  color: theme.palette[color].main,
                  width: 48,
                  height: 48,
                  boxShadow: `0 4px 12px ${alpha(theme.palette[color].main, 0.2)}`,
                }}
              >
                {icon}
              </Avatar>
              {trend && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {trend === 'up' ? (
                    <TrendingUp sx={{ color: 'success.main' }} />
                  ) : (
                    <TrendingDown sx={{ color: 'error.main' }} />
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      color: trend === 'up' ? 'success.main' : 'error.main',
                      fontWeight: 500,
                    }}
                  >
                    {trendValue}
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography variant="h6" gutterBottom color="text.secondary" sx={{ fontSize: '0.875rem' }}>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              {value}
            </Typography>
          </>
        )}
      </CardContent>
    </StyledCard>
  ;

  return (
    <Box sx={{ 
      padding: { xs: 2, sm: 3 },
      minHeight: '100vh',
      bgcolor: 'background.default'
    }}>
      {/* Error Snackbar */}
      <Snackbar 
        open={!!errorMessage} 
        autoHideDuration={6000} 
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setErrorMessage('')} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Premium Welcome Card */}
      <GlassCard
        sx={{
          mb: 4,
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 4,
          boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.9)} 100%)`,
          backdropFilter: 'blur(10px)',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        }}
      >
        {/* Decorative elements */}
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.1)} 0%, transparent 70%)`,
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            background: 'url(/images/pattern.svg)',
            opacity: 0.05,
            zIndex: 0,
          }}
        />
        
        <CardContent sx={{ position: 'relative', p: { xs: 3, md: 4 }, zIndex: 1 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
                <Avatar
                  src={user?.profile_image}
                  alt={user?.name}
                  sx={{
                    width: 80,
                    height: 80,
                    border: `4px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.2)}`,
                    bgcolor: theme.palette.primary.main,
                    fontSize: '2rem',
                    fontWeight: 600,
                  }}
                >
                  {user?.name?.charAt(0) || 'D'}
                </Avatar>
                <Box>
                  <Typography 
                    variant="h3" 
                    sx={{ 
                      fontWeight: 800,
                      fontSize: { xs: '1.75rem', sm: '2.25rem' },
                      color: theme.palette.primary.dark,
                      letterSpacing: '-0.5px',
                      mb: 0.5,
                    }}
                  >
                    {getGreeting()}, {greetingName}!
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: theme.palette.text.secondary,
                      fontWeight: 500,
                    }}
                  >
                    {todayAppointments.length > 0 
                      ? `You have ${todayAppointments.length} appointment${todayAppointments.length === 1 ? '' : 's'} scheduled for today`
                      : "You have no appointments scheduled for today"}
                  </Typography>
                </Box>
              </Box>
              
              {/* Quick stats */}
              <Box 
                sx={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: 2, 
                  mb: 3,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      color: theme.palette.success.main,
                      width: 36,
                      height: 36,
                    }}
                  >
                    <CheckIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      Confirmed
                    </Typography>
                    <Typography variant="h6" sx={{ color: theme.palette.success.dark, fontWeight: 700 }}>
                      {dashboardStats.data?.confirmed || 0}
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                      color: theme.palette.warning.main,
                      width: 36,
                      height: 36,
                    }}
                  >
                    <PendingIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      Pending
                    </Typography>
                    <Typography variant="h6" sx={{ color: theme.palette.warning.dark, fontWeight: 700 }}>
                      {dashboardStats.data?.pending || 0}
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.error.main, 0.1),
                      color: theme.palette.error.main,
                      width: 36,
                      height: 36,
                    }}
                  >
                    <CancelIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      Canceled
                    </Typography>
                    <Typography variant="h6" sx={{ color: theme.palette.error.dark, fontWeight: 700 }}>
                      {dashboardStats.data?.canceled || 0}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              
              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={2}
              >
                <ActionButton
                  variant="contained"
                  startIcon={<Event />}
                  onClick={() => navigate('/doctor-dashboard/appointments')}
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.common.white,
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Manage Schedule
                </ActionButton>
                <ActionButton
                  variant="outlined"
                  startIcon={<Assessment />}
                  sx={{
                    borderColor: alpha(theme.palette.primary.main, 0.5),
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                  onClick={() => navigate('/doctor-dashboard/consultation-history')}
                >
                  View Reports
                </ActionButton>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    width: 200,
                    height: 200,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
                    animation: 'pulse 3s infinite',
                    '@keyframes pulse': {
                      '0%': { transform: 'scale(1)', opacity: 0.5 },
                      '50%': { transform: 'scale(1.1)', opacity: 0.3 },
                      '100%': { transform: 'scale(1)', opacity: 0.5 },
                    },
                  }}
                />
                <DoctorIcon sx={{ 
                  fontSize: 200, 
                  opacity: 0.1,
                  color: theme.palette.primary.main,
                  animation: 'float 4s ease-in-out infinite',
                  '@keyframes float': {
                    '0%': { transform: 'translateY(0px)' },
                    '50%': { transform: 'translateY(-15px)' },
                    '100%': { transform: 'translateY(0px)' },
                  },
                }} />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </GlassCard>

      {/* Quick Actions */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <ActionButton
            fullWidth
            variant="outlined"
            color="primary"
            startIcon={<VideoCall />}
            onClick={() => navigate('/doctor-dashboard/consultation')}
            sx={{ p: 2, height: '100%' }}
          >
            Start A Consultation
          </ActionButton>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ActionButton
            fullWidth
            variant="outlined"
            color="secondary"
            startIcon={<Chat />}
            onClick={() => navigate('/doctor-dashboard/patients')}
            sx={{ p: 2, height: '100%' }}
          >
            Patient Management
          </ActionButton>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ActionButton
            fullWidth
            variant="outlined"
            color="info"
            startIcon={<MedicalServices />}
            onClick={() => navigate('/doctor-dashboard/availability')}
            sx={{ p: 2, height: '100%' }}
          >
            Manage Availability
          </ActionButton>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ActionButton
            fullWidth
            variant="outlined"
            color="success"
            startIcon={<Assessment />}
            onClick={() => navigate('/doctor-dashboard/billing')}
            sx={{ p: 2, height: '100%' }}
          >
            Billing & Invoices
          </ActionButton>
        </Grid>
      </Grid>

      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Today's Appointments"
            value={dashboardStats.data?.today?.count || 0}
            icon={<CalendarIcon />}
            color="primary"
            loading={dashboardStats.loading}
            trend={dashboardStats.data?.today?.trend > 0 ? 'up' : 'down'}
            trendValue={dashboardStats.data?.today?.trend_text || ''}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Confirmed"
            value={dashboardStats.data?.confirmed || 0}
            icon={<CheckIcon />}
            color="success"
            loading={dashboardStats.loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending"
            value={dashboardStats.data?.pending || 0}
            icon={<PendingIcon />}
            color="warning"
            loading={dashboardStats.loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Canceled"
            value={dashboardStats.data?.canceled || 0}
            icon={<CancelIcon />}
            color="error"
            loading={dashboardStats.loading}
          />
        </Grid>
      </Grid>

      {/* Subscription Status Card */}
      {(isOnTrial() || hasActiveSubscription()) && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <StyledCard>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Subscription Status
                  </Typography>
                  <Chip 
                    label={isOnTrial() ? 'Trial' : getPlanType().toUpperCase()}
                    color={isOnTrial() ? 'warning' : 'primary'}
                    size="small"
                  />
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center">
                      <Typography variant="h6" color="primary">
                        {getCurrentUsage('appointments')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Appointments Used
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={getUsagePercentage('appointments')}
                        color={isUsageLimitReached('appointments') ? 'error' : isUsageLimitNear('appointments') ? 'warning' : 'primary'}
                        sx={{ mt: 1, height: 4 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center">
                      <Typography variant="h6" color="primary">
                        {getCurrentUsage('patients')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Patients Managed
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={getUsagePercentage('patients')}
                        color={isUsageLimitReached('patients') ? 'error' : isUsageLimitNear('patients') ? 'warning' : 'primary'}
                        sx={{ mt: 1, height: 4 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center">
                      <Typography variant="h6" color="primary">
                        {getCurrentUsage('consultations')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Consultations
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={getUsagePercentage('consultations')}
                        color={isUsageLimitReached('consultations') ? 'error' : isUsageLimitNear('consultations') ? 'warning' : 'primary'}
                        sx={{ mt: 1, height: 4 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center">
                      <Typography variant="h6" color="primary">
                        {isOnTrial() ? getDaysRemaining() : '∞'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {isOnTrial() ? 'Days Remaining' : 'Unlimited'}
                      </Typography>
                      {isOnTrial() && (
                        <LinearProgress 
                          variant="determinate" 
                          value={(getDaysRemaining() / 30) * 100}
                          color="warning"
                          sx={{ mt: 1, height: 4 }}
                        />
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>
      )}

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <StyledCard>
            <CardContent>
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Patient & Revenue Trends</Typography>
                <IconButton size="small">
                  <MoreVert />
                </IconButton>
              </Box>
              {dashboardTrends.loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <CircularProgress />
                </Box>
              ) : dashboardTrends.error ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300, flexDirection: 'column', gap: 2 }}>
                  <ErrorIcon sx={{ fontSize: 48, color: 'error.main', opacity: 0.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    Unable to load trends data
                  </Typography>
                </Box>
              ) : (dashboardTrends.data || []).length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <Typography variant="body2" color="text.secondary">
                    No trends data available
                  </Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={dashboardTrends.data || []}>
                    <defs>
                      <linearGradient id="colorNewPatients" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.8}/>
                        <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorFollowUps" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={theme.palette.secondary.main} stopOpacity={0.8}/>
                        <stop offset="95%" stopColor={theme.palette.secondary.main} stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.primary, 0.1)} />
                    <XAxis dataKey="month" stroke={theme.palette.text.secondary} />
                    <YAxis stroke={theme.palette.text.secondary} />
                    <ChartTooltip />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="new_patients"
                      name="New Patients"
                      stroke={theme.palette.primary.main}
                      fillOpacity={1}
                      fill="url(#colorNewPatients)"
                    />
                    <Area
                      type="monotone"
                      dataKey="follow_ups"
                      name="Follow-ups"
                      stroke={theme.palette.secondary.main}
                      fillOpacity={1}
                      fill="url(#colorFollowUps)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <StyledCard>
            <CardContent>
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Consultation Types</Typography>
                <IconButton size="small">
                  <MoreVert />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {consultationTypes.loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                    <CircularProgress />
                  </Box>
                ) : consultationTypes.error ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200, flexDirection: 'column', gap: 2 }}>
                    <ErrorIcon sx={{ fontSize: 48, color: 'error.main', opacity: 0.5 }} />
                    <Typography variant="body2" color="text.secondary">
                      Unable to load distribution data
                    </Typography>
                  </Box>
                ) : (consultationTypes.data || []).length === 0 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                    <Typography variant="body2" color="text.secondary">
                      No consultation data available
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={consultationTypes.data || []}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          fill="#8884d8"
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {(consultationTypes.data || []).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <ChartTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    <Box sx={{ mt: 2, width: '100%' }}>
                      <Grid container spacing={1}>
                        {(consultationTypes.data || []).map((type) => (
                          <Grid item xs={6} key={type.name}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: '50%',
                                  bgcolor: type.color,
                                }}
                              />
                              <Typography variant="body2" color="text.secondary">
                                {type.name}: {type.value}%
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </>
                )}
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Premium Schedule Card */}
      <StyledCard
        sx={{
          borderRadius: 4,
          boxShadow: `0 10px 30px ${alpha(theme.palette.primary.main, 0.1)}`,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <Box
          sx={{
            p: 3,
            background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.background.paper, 0)} 100%)`,
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Box sx={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  width: 48,
                  height: 48,
                  boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
              >
                <CalendarIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.dark }}>
                  Today's Schedule
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {format(new Date(), 'EEEE, MMMM d, yyyy')}
                </Typography>
              </Box>
            </Box>
            <ActionButton
              variant="contained"
              color="primary"
              endIcon={<ArrowForward />}
              onClick={() => navigate('/doctor-dashboard/appointments')}
              sx={{
                borderRadius: 2,
                px: 2,
                py: 1,
                boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.2)}`,
                '&:hover': {
                  transform: 'translateX(4px)',
                  boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                },
              }}
            >
              Manage Schedule
            </ActionButton>
          </Box>
        </Box>
        <Box sx={{ p: 3 }}>
          {appointmentsLoading ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <CircularProgress size={40} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Loading appointments...
              </Typography>
            </Box>
          ) : todayAppointments.length > 0 ? (
            <List sx={{ width: '100%' }}>
              {todayAppointments.slice(0, 5).map((appointment) => (
                <ListItem
                  key={appointment.id}
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    bgcolor: alpha(theme.palette.background.paper, 0.5),
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      transform: 'translateX(4px)',
                      boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
                    },
                  }}
                  secondaryAction={
                    <Stack direction="row" spacing={1}>
                      <Chip
                        label={appointment.status}
                        size="small"
                        color={
                          appointment.status === 'Confirmed'
                            ? 'success'
                            : appointment.status === 'Pending'
                            ? 'warning'
                            : 'default'
                        }
                        sx={{ fontWeight: 600 }}
                      />
                      {appointment.status === 'Confirmed' && (
                        <ActionButton
                          variant="contained"
                          size="small"
                          startIcon={<VideoCall />}
                          onClick={() => handleStartConsultation(appointment)}
                          sx={{
                            borderRadius: 1.5,
                            textTransform: 'none',
                            px: 2,
                            py: 0.5,
                          }}
                        >
                          Start
                        </ActionButton>
                      )}
                    </Stack>
                  }
                >
                  <ListItemAvatar>
                    <PatientAvatar>
                      {appointment.patient_name?.charAt(0) || appointment.patient?.name?.charAt(0) || 'P'}
                    </PatientAvatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {appointment.patient_name || appointment.patient?.name || 'Unknown Patient'}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {appointment.scheduled_time || appointment.time || 'Time TBD'} • {appointment.type || 'Consultation'}
                        </Typography>
                        {appointment.reason && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            {appointment.reason}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
              {todayAppointments.length > 5 && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Button
                    variant="text"
                    onClick={() => navigate('/doctor-dashboard/appointments')}
                    endIcon={<ArrowForward />}
                  >
                    View all {todayAppointments.length} appointments
                  </Button>
                </Box>
              )}
            </List>
          ) : (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <CalendarIcon sx={{ fontSize: 64, color: theme.palette.text.secondary, opacity: 0.3, mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No appointments scheduled for today
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Your schedule is clear for today
              </Typography>
              <ActionButton
                variant="outlined"
                color="primary"
                onClick={() => navigate('/doctor-dashboard/appointments')}
              >
                View All Appointments
              </ActionButton>
            </Box>
          )}
        </Box>
      </StyledCard>
    </Box>
  );
};

export default DoctorDashboard;